import { apiClient } from '../api-client';
import { ApiResponse, ApiError } from '../../dtos/common/api.dto';
import type {
  ClaimWorksheet,
  BillingConfig,
  BillingReadiness,
  Claim,
  ClaimDetail,
  ClaimDownloadResponse,
  ClaimsListParams,
  ClaimsListResponse,
  CorrectClaimRequest,
  GenerateClaimsResponse,
  ReadinessBlock,
  UnbilledVisits,
  ServiceCodeRow,
  UpdateClaimStatusRequest,
} from '../../dtos/billing/claims.dto';
import { isEvvUnitsMatched } from '../../dtos/billing/claims.dto';
import type { DisciplineCode, ProviderTypeCode } from '../../constants/disciplineRegistry';

/** A visit's discipline as billed: a registry discipline or provider type (LVN, PTA, COTA, …) */
export type Discipline = DisciplineCode | ProviderTypeCode;

// ── Generation errors ────────────────────────────────────────────────────────

/**
 * Why claims could not be generated, parsed from the API error.
 * - notReady: 409 CLAIM_NOT_READY with each visit's blocks (box named where it is a form field)
 * - alreadyBilled: 409 VISIT_ALREADY_BILLED (someone else billed a selected visit first)
 * - mixedPayers: 400 MIXED_PAYERS
 * - other: anything else
 */
export type ClaimGenerationError =
  | { kind: 'notReady'; message: string; missing: string[]; visits: Array<{ visitId: string; visitDate: string | null; blocks: ReadinessBlock[] }> }
  | { kind: 'alreadyBilled'; message: string }
  | { kind: 'mixedPayers'; message: string }
  | { kind: 'other'; message: string };

function errorCode(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    const detailCode = typeof err.details?.code === 'string' ? err.details.code : undefined;
    return err.domainCode || detailCode;
  }
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : undefined;
}

function unwrap<T>(response: ApiResponse<T>, fallback: string): T {
  if (response.success && response.data !== undefined && response.data !== null) return response.data;
  throw new Error(response.error?.message || fallback);
}

/** Display text for a readiness block: "Box 17: …" when a CMS-1500 box is named. */
export function blockText(block: ReadinessBlock): string {
  return block.box ? `Box ${block.box}: ${block.message}` : block.message;
}

/**
 * Claims Service — claim readiness, generation, the claims register and billing config
 * (Phase 11). Component → ClaimsService → apiClient.
 */
/** Phase 22 (EVV G6): a row with evvUnitsMatched as a boolean (absent or non-boolean = true). */
function withUnitsMatchedFlag(row: ServiceCodeRow): ServiceCodeRow {
  return { ...row, evvUnitsMatched: isEvvUnitsMatched(row) };
}

function withEvvUnitsMatched(config: BillingConfig): BillingConfig {
  return Array.isArray(config?.serviceCodes) ? { ...config, serviceCodes: config.serviceCodes.map(withUnitsMatchedFlag) } : config;
}

export class ClaimsService {
  /** The single readiness source for a patient (BIL-01..05); also the BIL-02 preflight. */
  static async getReadiness(patientId: string): Promise<BillingReadiness> {
    const response: ApiResponse<BillingReadiness> = await apiClient.get('/billing/claims/visits-ready', { patientId });
    return unwrap(response, 'Failed to load billing readiness');
  }

  /** Generate CMS-1500 claims for ready visits of one patient and payer (≤ 6 lines per claim). */
  static async generateClaims(visitIds: string[]): Promise<GenerateClaimsResponse> {
    const response: ApiResponse<GenerateClaimsResponse> = await apiClient.post('/billing/claims/generate', { visitIds, format: 'cms1500' });
    return unwrap(response, 'Failed to generate claims');
  }

  static parseGenerationError(err: unknown): ClaimGenerationError {
    const message = err instanceof Error ? err.message : 'Failed to generate claims';
    const code = errorCode(err);
    const details = err instanceof ApiError ? err.details : (err as { details?: Record<string, unknown> } | null)?.details;
    if (code === 'CLAIM_NOT_READY') {
      const missing = Array.isArray(details?.missing) ? (details.missing as unknown[]).filter((m): m is string => typeof m === 'string') : [];
      const visits = Array.isArray(details?.visits) ? (details.visits as Array<{ visitId: string; visitDate: string | null; blocks: ReadinessBlock[] }>) : [];
      return { kind: 'notReady', message, missing, visits };
    }
    if (code === 'VISIT_ALREADY_BILLED') return { kind: 'alreadyBilled', message };
    if (code === 'MIXED_PAYERS') return { kind: 'mixedPayers', message };
    return { kind: 'other', message };
  }

  // ── Register ───────────────────────────────────────────────────────────────

  static async listClaims(params: ClaimsListParams = {}): Promise<ClaimsListResponse> {
    const query: Record<string, string | number> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query[key] = value as string | number;
    });
    const response: ApiResponse<ClaimsListResponse> = await apiClient.get('/billing/claims', query);
    return unwrap(response, 'Failed to load claims');
  }

  static async getDetail(claimId: string): Promise<ClaimDetail> {
    const response: ApiResponse<ClaimDetail> = await apiClient.get(`/billing/claims/${claimId}`);
    return unwrap(response, 'Failed to load the claim');
  }

  /** Manual status update with date / method / amount / reason, or only the secondary-billed flag. */
  static async updateClaimStatus(claimId: string, payload: UpdateClaimStatusRequest): Promise<Claim> {
    const response: ApiResponse<Claim> = await apiClient.patch(`/billing/claims/${claimId}/status`, payload);
    return unwrap(response, 'Failed to update the claim');
  }

  /** Void an unsubmitted claim; its visits become billable again. */
  static async voidClaim(claimId: string, reason: string): Promise<Claim> {
    const response: ApiResponse<Claim> = await apiClient.post(`/billing/claims/${claimId}/void`, { reason });
    return unwrap(response, 'Failed to void the claim');
  }

  /** Corrected claim: a new version with frequency 7 (replace) or 8 (void). */
  static async correctClaim(claimId: string, payload: CorrectClaimRequest): Promise<{ original: Claim; claim: Claim }> {
    const response: ApiResponse<{ original: Claim; claim: Claim }> = await apiClient.post(`/billing/claims/${claimId}/correct`, payload);
    return unwrap(response, 'Failed to create the corrected claim');
  }

  /** Phase 17 (REQ-CLM-07): claim values in the submission portal's entry order (audited view). */
  static async getClaimWorksheet(claimId: string): Promise<ClaimWorksheet> {
    const response: ApiResponse<ClaimWorksheet> = await apiClient.get(`/billing/claims/${claimId}/worksheet`);
    return unwrap(response, 'Failed to load the claim worksheet');
  }

  /** Short-lived pre-signed URL for the claim PDF (CMS-1500, or the UB-04 keying sheet). */
  static async getClaimDownloadUrl(claimId: string): Promise<ClaimDownloadResponse> {
    const response: ApiResponse<ClaimDownloadResponse> = await apiClient.get(`/billing/claims/${claimId}/download`);
    return unwrap(response, 'Failed to get the download link');
  }

  /** DSC-03: completed visits of a patient not yet on a claim (discharge readiness). */
  static async getUnbilledVisits(patientId: string): Promise<UnbilledVisits> {
    const response: ApiResponse<UnbilledVisits> = await apiClient.get(`/patientmr/${patientId}/billing/unbilled`);
    return unwrap(response, 'Failed to load unbilled visits');
  }

  // ── Billing configuration ──────────────────────────────────────────────────

  static async getBillingConfig(): Promise<BillingConfig> {
    const response: ApiResponse<BillingConfig> = await apiClient.get('/billing/config');
    return withEvvUnitsMatched(unwrap(response, 'Failed to load the billing configuration'));
  }

  static async saveBillingConfig(config: BillingConfig): Promise<BillingConfig> {
    const { agingDays, payerRules } = config;
    // Phase 22 (EVV G6): evvUnitsMatched always goes as a boolean (absent = true)
    const serviceCodes = config.serviceCodes.map(withUnitsMatchedFlag);
    const response: ApiResponse<BillingConfig> = await apiClient.put('/billing/config', { serviceCodes, agingDays, payerRules });
    return withEvvUnitsMatched(unwrap(response, 'Failed to save the billing configuration'));
  }

  // ── Formatting ─────────────────────────────────────────────────────────────

  static formatCurrency(amount: number | null | undefined): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount || 0));
  }

  /** 'YYYY-MM-DD' → 'MM/DD/YYYY' without time-zone shifts. */
  static formatDate(dateOnly: string | null | undefined): string {
    const m = String(dateOnly || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
  }
}

export default ClaimsService;
